import { useCanvasById } from "@/api/canvas";
import { useAddFileRecordToLibrary } from "@/api/fileRecords";
import { useFlowSessionById } from "@/api/flow_sessions";
import { trackActionInvoked } from "@/object-actions/trackObjectAction";
import type {
  ObjectActionDestinationSubmenu,
  ResolvedObjectAction,
  TransferDestination,
} from "@/object-actions/types";
import type { FileRecord } from "@cohesive/api-client";
import { useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { useWritableWorkspaceIds } from "./useWritableWorkspaces";

/**
 * Add to workspace as a nested pop — the same destination cascade as Move to /
 * Copy to, with A3's structural difference: promotion never crosses
 * workspaces, so the workspace level is locked to the container's own (the
 * backend 422s anything else). A standalone chat with no workspace — the one
 * case the backend accepts an explicit choice — gets the full list, gated by
 * write access.
 *
 * Clicking the workspace row commits to the workspace root; drilling in picks
 * the folder. The file keeps its id and a reference stays behind (A3), which
 * is why the toast says "still available here".
 */
export function useAddToWorkspaceCommit(
  record: FileRecord | null,
  /** A block's live node box, when the surface can see the node — the
   *  promoted record remembers the size the user last saw. */
  displaySize?: { width: number; height: number },
): (destination: TransferDestination) => void {
  const addToLibrary = useAddFileRecordToLibrary();

  return useCallback(
    (destination: TransferDestination) => {
      if (!record?.id) return;
      trackActionInvoked("add-to-workspace", {
        objectKind: "file-record",
        objectSubtype: record.class,
      });
      addToLibrary.mutate(
        {
          record,
          workspaceId: destination.workspaceId,
          parentPath: destination.folderPath || undefined,
          displaySize,
        },
        {
          onSuccess: (data) => {
            // Name the landing folder so a silently-ignored parent_path can
            // never masquerade as success (same guard as the old dialog).
            const landed = data.file_record.path;
            const parent = landed.includes("/")
              ? landed.slice(0, landed.lastIndexOf("/"))
              : "";
            toast.success(
              parent
                ? `Added to Files › ${parent}`
                : "Added to the workspace — still available here",
            );
          },
          onError: () => toast.error("Failed to add to the workspace"),
        },
      );
    },
    [addToLibrary, record, displaySize],
  );
}

/**
 * The cascade descriptor for one record — shared by the registry attach and
 * hand-written menus (the canvas drawer). Null when the record can't promote
 * (already a workspace file or a reference). `enabled` gates the container
 * lookups so closed menus fetch nothing.
 */
export function useAddToWorkspaceDestinationSubmenu(
  record: FileRecord | null,
  enabled = true,
  displaySize?: { width: number; height: number },
): ObjectActionDestinationSubmenu | null {
  const commit = useAddToWorkspaceCommit(record, displaySize);

  const eligible =
    !!record?.id && record.class !== "workspace" && record.kind !== "link";
  const active = enabled && eligible;

  // The container's own workspace: canvas files via their canvas, chat files
  // via their session. Queries stay disabled until the menu wants them.
  const { data: canvas } = useCanvasById(
    active ? (record?.canvas_id ?? "") : "",
  );
  const { data: session } = useFlowSessionById(
    active ? (record?.flow_session_id ?? "") : "",
  );
  const homeWorkspaceId = record?.canvas_id
    ? canvas?.workspace_id
    : record?.flow_session_id
      ? session?.workspace_id
      : undefined;
  const containerHasWorkspace = !!record?.canvas_id || !!session?.workspace_id;

  // Only the no-home-workspace case offers a choice, so only it needs the
  // write-access gate.
  const writableWorkspaceIds = useWritableWorkspaceIds(
    active && !homeWorkspaceId && !containerHasWorkspace,
  );

  return useMemo(() => {
    if (!active) return null;
    return {
      kind: "workspace-destination",
      operation: "add-to-workspace",
      commit,
      // Locked to the home workspace once it resolves; an empty list renders
      // the loading row for the frame or two the container query needs.
      restrictToWorkspaceIds: homeWorkspaceId
        ? [homeWorkspaceId]
        : containerHasWorkspace
          ? []
          : undefined,
      writableWorkspaceIds:
        homeWorkspaceId || containerHasWorkspace
          ? undefined
          : writableWorkspaceIds,
    };
  }, [
    active,
    commit,
    containerHasWorkspace,
    homeWorkspaceId,
    writableWorkspaceIds,
  ]);
}

export function useAttachAddToWorkspaceSubmenu(
  actions: ResolvedObjectAction[],
  record: FileRecord | null,
  displaySize?: { width: number; height: number },
): ResolvedObjectAction[] {
  const hasAction = actions.some((a) => a.id === "add-to-workspace");
  const submenu = useAddToWorkspaceDestinationSubmenu(
    record,
    hasAction,
    displaySize,
  );

  return useMemo(() => {
    if (!submenu || !hasAction) return actions;
    return actions.map((action) =>
      action.id === "add-to-workspace" ? { ...action, submenu } : action,
    );
  }, [actions, hasAction, submenu]);
}

/**
 * Group promotion for the canvas multi-select toolbar: one cascade (all the
 * records live on one canvas, so they share a home workspace — the
 * descriptor is built from the first), one commit looping every promotable
 * record, one summary toast. Ineligible selection members never reach here —
 * the caller filters to container-class plain files.
 */
export function useAttachBulkAddToWorkspaceSubmenu(
  actions: ResolvedObjectAction[],
  records: FileRecord[],
  resolveDisplaySize?: (
    record: FileRecord,
  ) => { width: number; height: number } | undefined,
): ResolvedObjectAction[] {
  const hasAction = actions.some((a) => a.id === "add-to-workspace");
  const addToLibrary = useAddFileRecordToLibrary();
  const base = useAddToWorkspaceDestinationSubmenu(
    records[0] ?? null,
    hasAction && records.length > 0,
  );

  const commit = useCallback(
    (destination: TransferDestination) => {
      trackActionInvoked("add-to-workspace", {
        objectKind: "multi-selection",
        selectionCount: records.length,
      });
      void (async () => {
        let failed = 0;
        for (const record of records) {
          try {
            await addToLibrary.mutateAsync({
              record,
              workspaceId: destination.workspaceId,
              parentPath: destination.folderPath || undefined,
              displaySize: resolveDisplaySize?.(record),
            });
          } catch {
            failed += 1;
          }
        }
        const succeeded = records.length - failed;
        if (failed === 0) {
          toast.success(
            succeeded === 1
              ? "Added to the workspace — still available here"
              : `Added ${succeeded} to the workspace — still available here`,
          );
        } else {
          toast.error(
            `Added ${succeeded} of ${records.length} to the workspace.`,
          );
        }
      })();
    },
    [addToLibrary, records, resolveDisplaySize],
  );

  return useMemo(() => {
    if (!base || !hasAction) return actions;
    const submenu: ObjectActionDestinationSubmenu = { ...base, commit };
    return actions.map((action) =>
      action.id === "add-to-workspace" ? { ...action, submenu } : action,
    );
  }, [actions, base, commit, hasAction]);
}
